export const GENDERS = ["women", "men", "unisex"];
export const AGES = ["infant", "toddler", "kid", "teen", "adult"];
export const PRODUCT_TYPES = ["apparel", "shoes", "hats", "ties", "wallets", "socks", "handbags",
  "watches", "bracelets", "earrings", "necklaces", "rings"];
export const COLORS = ["black", "white", "beige", "navy", "red", "green", "blue", "brown", "pink", "gray"];
export const PATTERNS = ["solid", "striped", "floral", "geometric", "graphic", "checked", "paisley", "other"];
export const STYLES = ["casual", "formal", "streetwear", "athletic", "vintage", "luxury"];
export const CONDITIONS = ["new", "used"];
export const SIZES = ["XS", "S", "M", "L", "XL", "XXL", "US 7", "US 8", "US 9", "US 10", "US 11", "One size"];
export const MATERIALS = ["cotton", "linen", "silk", "wool", "cashmere", "denim", "leather", "suede", "polyester", "knit", "velvet"];

// ---- Guided Shoes shopping ----
// Audience is a shopper-friendly rollup of gender + age_group used by the Shoes flow.
export const SHOE_AUDIENCES = [
  { key: "womens", label: "Women's", note: "Adult" },
  { key: "mens", label: "Men's", note: "Adult" },
  { key: "kids", label: "Kids", note: "Children" },
  { key: "junior", label: "Junior", note: "Teen" },
  { key: "baby", label: "Baby", note: "Infant & toddler" },
];
export const SHOE_AUDIENCE_LABELS = SHOE_AUDIENCES.reduce((a, s) => ({ ...a, [s.key]: s.label }), {});
// US shoe sizes per audience. Audience already narrows gender/age, so plain values are unambiguous.
export const SHOE_SIZES = {
  womens: ["US 5", "US 5.5", "US 6", "US 6.5", "US 7", "US 7.5", "US 8", "US 8.5", "US 9", "US 9.5", "US 10", "US 10.5", "US 11", "US 12"],
  mens:   ["US 7", "US 7.5", "US 8", "US 8.5", "US 9", "US 9.5", "US 10", "US 10.5", "US 11", "US 11.5", "US 12", "US 13", "US 14", "US 15"],
  junior: ["US 3.5Y", "US 4Y", "US 4.5Y", "US 5Y", "US 5.5Y", "US 6Y", "US 6.5Y", "US 7Y"],
  kids:   ["US 10.5C", "US 11C", "US 11.5C", "US 12C", "US 12.5C", "US 13C", "US 1Y", "US 2Y", "US 3Y"],
  baby:   ["US 1C", "US 2C", "US 3C", "US 4C", "US 5C", "US 6C", "US 7C", "US 8C", "US 9C", "US 10C"],
};
// Map a listing's gender + age_group to a shoe-audience key.
export function shoeAudience(gender, age) {
  if (age === "infant" || age === "toddler") return "baby";
  if (age === "kid") return "kids";
  if (age === "teen") return "junior";
  if (gender === "men") return "mens";
  return "womens";
}
// Which shoe sizes to offer a seller, given the gender + age they picked.
export function shoeSizesFor(gender, age) {
  if (age === "adult" && gender === "unisex") {
    return Array.from(new Set([...SHOE_SIZES.womens, ...SHOE_SIZES.mens]));
  }
  return SHOE_SIZES[shoeAudience(gender, age)] || SHOE_SIZES.womens;
}

// ---- Guided clothing (apparel) shopping by department ----
export const DEPTS = ["women", "men", "kids"];
export const DEPT_LABELS = { women: "Women", men: "Men", kids: "Kids" };
export const GARMENTS = {
  women: [
    { key: "dresses", label: "Dresses" },
    { key: "tops", label: "Tops & Blouses" },
    { key: "tshirts", label: "T-shirts" },
    { key: "sweaters", label: "Sweaters & Knits" },
    { key: "pants", label: "Pants & Trousers" },
    { key: "jeans", label: "Jeans" },
    { key: "skirts", label: "Skirts" },
    { key: "shorts", label: "Shorts" },
    { key: "suits", label: "Suits & Blazers" },
    { key: "outerwear", label: "Jackets & Coats" },
    { key: "activewear", label: "Activewear" },
    { key: "nightwear", label: "Nightwear & Lingerie" },
    { key: "jumpsuits", label: "Jumpsuits" },
    { key: "swimwear", label: "Swimwear" },
  ],
  men: [
    { key: "shirts", label: "Shirts" },
    { key: "tshirts", label: "T-shirts" },
    { key: "polos", label: "Polos" },
    { key: "sweaters", label: "Sweaters & Knits" },
    { key: "pants", label: "Pants & Trousers" },
    { key: "jeans", label: "Jeans" },
    { key: "shorts", label: "Shorts" },
    { key: "suits", label: "Suits & Blazers" },
    { key: "outerwear", label: "Jackets & Coats" },
    { key: "activewear", label: "Activewear" },
    { key: "nightwear", label: "Nightwear" },
    { key: "swimwear", label: "Swimwear" },
  ],
  kids: [
    { key: "tops", label: "Tops" },
    { key: "tshirts", label: "T-shirts" },
    { key: "dresses", label: "Dresses" },
    { key: "sweaters", label: "Sweaters" },
    { key: "pants", label: "Pants & Jeans" },
    { key: "shorts", label: "Shorts" },
    { key: "skirts", label: "Skirts" },
    { key: "outerwear", label: "Jackets & Coats" },
    { key: "sleepwear", label: "Sleepwear" },
    { key: "activewear", label: "Activewear" },
    { key: "swimwear", label: "Swimwear" },
  ],
};
export const KID_GENDER_LABELS = { boys: "Boys", girls: "Girls" };
// Boys' clothing hides Dresses & Skirts; Girls' shows the full kids list.
export function kidsGarments(kidGender) {
  if (kidGender === "boys") return GARMENTS.kids.filter((g) => g.key !== "dresses" && g.key !== "skirts");
  return GARMENTS.kids;
}
export const CLOTHING_SIZES = {
  women: ["XS", "S", "M", "L", "XL", "XXL"],
  men: ["XS", "S", "M", "L", "XL", "XXL", "XXXL"],
  kids: ["2T", "3T", "4", "5", "6", "7", "8", "10", "12", "14"],
};
export function garmentLabel(dept, key) {
  const g = (GARMENTS[dept] || []).find((x) => x.key === key);
  return g ? g.label : key;
}
// The department (garment + size options) that applies to a gender + age on the Sell form.
export function apparelDept(gender, age) {
  if (age && age !== "adult") return "kids";
  if (gender === "men") return "men";
  return "women"; // women + unisex adult
}
export function garmentTypesFor(gender, age) {
  return GARMENTS[apparelDept(gender, age)] || GARMENTS.women;
}
export function clothingSizesFor(gender, age) {
  return CLOTHING_SIZES[apparelDept(gender, age)] || CLOTHING_SIZES.women;
}

// Pants/jeans are sized by waist + length (inseam) for adults (women/men). Kids bottoms keep numeric sizes.
export const BOTTOM_GARMENTS = ["pants", "jeans"];
export const WAIST_SIZES = {
  women: ["0", "2", "4", "6", "8", "10", "12", "14", "16", "24", "25", "26", "27", "28", "29", "30", "31", "32", "33", "34"],
  men: ["28", "29", "30", "31", "32", "33", "34", "36", "38", "40", "42", "44"],
};
export const INSEAM_SIZES = {
  women: ["28", "30", "32"],
  men: ["30", "32", "34", "36"],
};
export function isBottom(garment) {
  return BOTTOM_GARMENTS.includes(garment);
}
// True when this dept+garment should use waist/length instead of alpha/numeric sizes.
export function usesWaistLength(dept, garment) {
  return (dept === "women" || dept === "men") && isBottom(garment);
}

// ---- Quality standards ----
export const CONDITION_GRADES = ["nwt", "nwot", "excellent", "very_good", "good"]; // allowed to list (Good+)
export const GRADE_LABELS = {
  nwt: "New with tags", nwot: "New without tags", excellent: "Excellent",
  very_good: "Very good", good: "Good", fair: "Fair",
};
export const GRADE_HINTS = {
  nwt: "Unworn, original tags still attached.",
  nwot: "Unworn, tags removed — no signs of wear.",
  excellent: "Worn a handful of times. No visible flaws.",
  very_good: "Light, even wear. Any minor flaws pictured below.",
  good: "Honest, noticeable wear — every flaw must be photographed.",
  fair: "Heavy wear — not accepted on Your Classic Style.",
};
export const MEASUREMENT_SPECS = {
  apparel:   [["chest", "Chest / pit-to-pit", true], ["length", "Total length", true],
              ["shoulder", "Shoulder seam-to-seam", false], ["sleeve", "Sleeve length", false],
              ["waist", "Waist (laid flat)", false]],
  shoes:     [["size_label", "Size on label", true], ["insole", "Insole length", true]],
  handbags:  [["width", "Width", true], ["height", "Height", true],
              ["depth", "Depth", false], ["strap", "Strap drop", false]],
  watches:   [["case", "Case diameter", false], ["band", "Band / wrist length", false]],
  hats:      [["circumference", "Inner circumference", false]],
  ties:      [["length", "Length", true], ["width", "Width at widest", false]],
  wallets:   [["width", "Width", true], ["height", "Height", false]],
  socks:     [["size_label", "Size", false]],
  bracelets: [["length", "Length", false]],
  necklaces: [["length", "Chain length", false]],
  earrings:  [["drop", "Drop length", false]],
  rings:     [["size", "Ring size (US)", false], ["diameter", "Inner diameter", false]],
};
export const DEFAULT_MEASUREMENT_SPEC = [["size", "Size / key dimension", true]];

// ---- Accessory sizing ----
// Each accessory category filters/sells by a size scale that actually fits that product,
// instead of a generic clothing size list. Stored in the top-level `size` field.
export const ACCESSORY_SIZES = {
  watches:   { label: "Case size", options: ["34mm", "36mm", "38mm", "40mm", "42mm", "44mm", "46mm", "One size"] },
  rings:     { label: "Ring size (US)", options: ["4", "5", "6", "7", "8", "9", "10", "11", "12", "13"] },
  necklaces: { label: "Chain length", options: ["14\"", "16\"", "18\"", "20\"", "22\"", "24\"", "30\""] },
  bracelets: { label: "Wrist size", options: ["6\"", "6.5\"", "7\"", "7.5\"", "8\"", "One size"] },
  hats:      { label: "Hat size", options: ["XS", "S", "M", "L", "XL", "One size"] },
  socks:     { label: "Sock size", options: ["S", "M", "L", "XL", "One size"] },
};

// ---- Watch-specific attributes ----
export const WATCH_BANDS = ["metal", "leather", "rubber", "cloth"];
export const WATCH_BAND_LABELS = { metal: "Metal", leather: "Leather", rubber: "Rubber", cloth: "Cloth / NATO" };
export const WATCH_MOVEMENTS = ["automatic", "quartz", "mechanical", "solar"];
export const WATCH_MOVEMENT_LABELS = { automatic: "Automatic", quartz: "Quartz", mechanical: "Mechanical (hand-wound)", solar: "Solar" };
export const WATCH_DIAL_COLORS = ["black", "white", "silver", "blue", "green", "gold", "brown", "gray"];
export const WATCH_WATER_RESISTANCE = ["30m", "50m", "100m", "200m+"];

// ---- Jewelry-specific attributes ----
export const JEWELRY_TYPES = ["rings", "necklaces", "bracelets", "earrings"];
export const JEWELRY_METALS = ["gold", "silver", "rose_gold", "white_gold", "platinum", "other"];
export const JEWELRY_METAL_LABELS = { gold: "Gold", silver: "Silver", rose_gold: "Rose gold", white_gold: "White gold", platinum: "Platinum", other: "Other" };
export const JEWELRY_STONES = ["none", "diamond", "sapphire", "ruby", "emerald", "pearl", "opal", "other"];
export const JEWELRY_STONE_LABELS = { none: "No stone", diamond: "Diamond", sapphire: "Sapphire", ruby: "Ruby", emerald: "Emerald", pearl: "Pearl", opal: "Opal", other: "Other" };
export const JEWELRY_HALLMARKS = ["9ct", "14ct", "18ct", "22ct", "925", "950", "other"];
export const JEWELRY_HALLMARK_LABELS = { "9ct": "9ct gold", "14ct": "14ct gold", "18ct": "18ct gold", "22ct": "22ct gold", "925": "925 sterling silver", "950": "950 platinum", other: "Other / unmarked" };

// ---- Wallet styles ----
export const WALLET_STYLES = ["bifold", "trifold", "cardholder", "coin_purse", "long", "zip_around", "money_clip", "checkbook", "travel"];
export const WALLET_STYLE_LABELS = { bifold: "Bifold", trifold: "Trifold", cardholder: "Cardholder", coin_purse: "Coin purse", long: "Long / continental", zip_around: "Zip-around", money_clip: "Money clip", checkbook: "Checkbook", travel: "Travel wallet" };
export const WALLET_CARD_SLOTS = ["1-3", "4-6", "7-9", "10+"];

// ---- Handbag styles & sizes ----
export const HANDBAG_STYLES = ["tote", "crossbody", "shoulder", "clutch", "satchel", "backpack", "hobo", "bucket", "top_handle"];
export const HANDBAG_STYLE_LABELS = { tote: "Tote", crossbody: "Crossbody", shoulder: "Shoulder bag", clutch: "Clutch", satchel: "Satchel", backpack: "Backpack", hobo: "Hobo", bucket: "Bucket", top_handle: "Top-handle" };
export const HANDBAG_SIZES = ["small", "medium", "large", "oversized"];
export const HANDBAG_SIZE_LABELS = { small: "Small", medium: "Medium", large: "Large", oversized: "Oversized" };

// ---- Tie styles ----
export const TIE_STYLES = ["skinny", "standard", "wide", "bow", "knit"];
export const TIE_STYLE_LABELS = { skinny: "Skinny", standard: "Standard", wide: "Wide", bow: "Bow tie", knit: "Knit" };

// Combined label map for the accessory spec panel (garment_type across accessory categories).
export const ACCESSORY_STYLE_LABELS = { ...WALLET_STYLE_LABELS, ...HANDBAG_STYLE_LABELS, ...TIE_STYLE_LABELS };
export const APPAREL_BOTTOMS_SPEC = [["waist", "Waist (laid flat)", true], ["rise", "Front rise", false], ["inseam", "Inseam", true], ["leg", "Leg opening", false]];
export function measurementSpec(ptype, garment) {
  if (ptype === "apparel" && (garment === "pants" || garment === "jeans")) return APPAREL_BOTTOMS_SPEC;
  return MEASUREMENT_SPECS[ptype] || FABRIC_MEASUREMENT_SPECS[ptype] || DEFAULT_MEASUREMENT_SPEC;
}

// ---- Categories (top-level marketplace areas — built for growth) ----
export const CATEGORIES = [
  { key: "marketplace", label: "Marketplace" },
  { key: "fabric_archives", label: "Fabric Archives" },
  { key: "home_decor", label: "Home Decor" },
];
export const FABRIC_TYPES = ["fabric", "lace_trim", "notions", "pattern", "heirloom_textile", "cultural_fabric"];
export const FABRIC_TYPE_LABELS = {
  fabric: "Sewing fabric / yardage",
  lace_trim: "Lace & trim",
  notions: "Sewing notions",
  pattern: "Sewing patterns",
  heirloom_textile: "Heirloom textiles",
  cultural_fabric: "Cultural & world fabrics",
};
export const FABRIC_TYPE_BLURBS = {
  fabric: "Upscale yardage — silks, wools, brocades, cottons by the metre.",
  lace_trim: "Antique and heritage lace, ribbon, braid and passementerie.",
  notions: "Buttons, buckles, thread, hooks — the finishing details.",
  pattern: "Vintage and out-of-print sewing patterns.",
  heirloom_textile: "Quilts, linens, embroidered and hand-worked textiles.",
  cultural_fabric: "Culturally unique and world textiles with provenance.",
};
export const FABRIC_MEASUREMENT_SPECS = {
  fabric:           [["width", "Fabric width", true], ["length", "Length / yardage", true], ["fiber", "Fiber content", false]],
  lace_trim:        [["width", "Trim width", true], ["length", "Length", true], ["fiber", "Material", false]],
  notions:          [["quantity", "Quantity / count", true], ["size", "Size / gauge", false]],
  pattern:          [["size", "Pattern size(s)", false], ["garment", "Garment type", false]],
  heirloom_textile: [["width", "Width", true], ["length", "Length", true], ["fiber", "Material", false]],
  cultural_fabric:  [["width", "Width", true], ["length", "Length / yardage", true], ["origin", "Origin / culture", false]],
};

export const ERAS = ["80s", "90s", "y2k", "modern"];

// ---- Sewing patterns (Fabric Archives > Sewing patterns) ----
export const PATTERN_LETTER_SIZES = ["XS", "S", "M", "L", "XL", "XXL"];
export const PATTERN_NUMERIC_SIZES = ["2", "4", "6", "8", "10", "12", "14", "16", "18", "20", "22", "24"];
export const PATTERN_WEARERS = [
  { key: "womens", label: "Women's", gender: "women", age: "adult", sexed: false },
  { key: "mens", label: "Men's", gender: "men", age: "adult", sexed: false },
  { key: "teens", label: "Teens", gender: "unisex", age: "teen", sexed: true },
  { key: "kids", label: "Kids", gender: "unisex", age: "kid", sexed: true },
  { key: "babies", label: "Babies", gender: "unisex", age: "infant", sexed: true },
];
export const PATTERN_CATEGORIES = [
  ["curtains", "Curtains"], ["linens", "Linens"], ["tablecloths", "Tablecloths"],
  ["tote_bags", "Tote bags"], ["other_pattern", "Other patterns"],
];
export const PATTERN_CATEGORY_LABELS = PATTERN_CATEGORIES.reduce((a, [k, l]) => ({ ...a, [k]: l }), {});
export const PATTERN_DIFFICULTY = [
  ["beginner", "Beginner"], ["intermediate", "Intermediate"], ["advanced", "Advanced"],
];
export const PATTERN_DIFFICULTY_LABELS = PATTERN_DIFFICULTY.reduce((a, [k, l]) => ({ ...a, [k]: l }), {});
export const PATTERN_CLOTHING = [
  ["dresses", "Dresses"], ["tops", "Tops & blouses"], ["shirts", "Shirts"],
  ["skirts", "Skirts"], ["trousers", "Trousers & pants"], ["shorts", "Shorts"],
  ["jackets", "Jackets & coats"], ["jumpsuits", "Jumpsuits & rompers"],
  ["nightwear", "Nightwear"], ["activewear", "Activewear"], ["swimwear", "Swimwear"],
];
export const PATTERN_CLOTHING_LABELS = PATTERN_CLOTHING.reduce((a, [k, l]) => ({ ...a, [k]: l }), {});

export const ERA_LABELS = {
  "80s": "1980s",
  "90s": "1990s",
  y2k: "Y2K",
  modern: "Modern",
  unspecified: "Era unknown",
};

export const PACKAGE_SIZES = ["small", "medium", "large", "xl"];
export const PACKAGE_SIZE_LABELS = {
  small: "Small — up to 1 lb (10×7×3 in)",
  medium: "Medium — up to 2 lb (13×11×4 in)",
  large: "Large — up to 5 lb (16×13×6 in)",
  xl: "Extra large — up to 10 lb (20×16×8 in)",
};
export const PACKAGE_SIZE_HINTS = {
  small: "Scarves, ties, wallets, jewelry, small accessories",
  medium: "Most clothing — tops, dresses, trousers",
  large: "Coats, boots, handbags, heavy knits",
  xl: "Bundles, bulky outerwear, multiple items",
};

// ---- Home Decor department ----
export const HOME_DECOR_CATEGORIES = ["cookware", "dinnerware", "utensils", "linens"];
export const HOME_DECOR_CATEGORY_LABELS = {
  cookware: "Cookware & Bakeware",
  dinnerware: "Dinnerware",
  utensils: "Utensils",
  linens: "Linens",
};
export const HOME_DECOR_CATEGORY_BLURBS = {
  cookware: "Pots, pans, Dutch ovens and vintage bakeware.",
  dinnerware: "Plates, bowls, serving pieces and tea sets.",
  utensils: "Flatware, cutlery, cooking tools and gadgets.",
  linens: "Tablecloths, napkins, runners and kitchen textiles.",
};
export const HOME_DECOR_ITEMS = {
  cookware: [
    ["pots_pans", "Pots & pans"], ["skillets", "Skillets & frying pans"], ["dutch_ovens", "Dutch ovens"],
    ["baking_sheets", "Baking sheets & trays"], ["casserole", "Casserole & bakeware dishes"],
    ["mixing_bowls", "Mixing bowls"], ["roasting_pans", "Roasting pans"], ["molds_tins", "Molds & tins"],
  ],
  dinnerware: [
    ["plates", "Plates"], ["bowls", "Bowls"], ["mugs_cups", "Mugs & cups"], ["cups_saucers", "Cups & saucers"],
    ["platters", "Serving platters"], ["tea_coffee_sets", "Tea & coffee sets"], ["serving_bowls", "Serving bowls"],
    ["pitchers", "Pitchers & jugs"], ["canisters", "Canisters"],
  ],
  utensils: [
    ["flatware", "Flatware & cutlery"], ["serving_spoons", "Serving spoons & ladles"],
    ["cooking_utensils", "Cooking utensils"], ["knives", "Knives"], ["kitchen_gadgets", "Kitchen gadgets & tools"],
    ["carving_sets", "Carving sets"],
  ],
  linens: [
    ["tablecloths", "Tablecloths"], ["napkins", "Napkins"], ["placemats", "Placemats"],
    ["table_runners", "Table runners"], ["tea_towels", "Kitchen & tea towels"], ["aprons", "Aprons"],
    ["doilies", "Doilies"],
  ],
};
export const HOME_DECOR_ITEM_LABELS = Object.values(HOME_DECOR_ITEMS).flat()
  .reduce((acc, [k, l]) => { acc[k] = l; return acc; }, {});


export const AUTH_CLAIMS = [
  {
    value: "authentic",
    title: "Authentic branded original",
    blurb: "You guarantee this is the real brand. It goes live right away with a verified badge — add proof close-ups to build buyer trust.",
  },
  {
    value: "unbranded",
    title: "Unbranded / Inspired by",
    blurb: "Vintage style with no brand claim. The brand field stays locked, so nobody can be misled.",
  },
  {
    value: "replica",
    title: "Replica (disclosed)",
    blurb: "Honestly disclosed reproduction. Buyers see a clear replica badge and can filter these out.",
  },
];

export const REPORT_REASONS = [
  "Logo or wordmark looks wrong",
  "Tag or inner label looks fake",
  "Stitching or hardware quality is off",
  "Item differs from the photos",
  "Brand claim is impossible for this era",
  "Price is impossibly low for this brand",
  "Other",
];

export const PROOF_HINTS = [
  "Brand tag / wordmark, filled frame",
  "Inner label with care, size & country codes",
  "Stitching close-up (seams, logo embroidery)",
  "Hardware, zip pulls, serial or date code",
];

const IMG = "https://static.prod-images.emergentagent.com/jobs/b4be43f3-2601-441c-9078-02b7f7e5431f/images/";

export const PROOF_EXAMPLES = [
  {
    key: "tag",
    img: IMG + "b32f91e71b01c0cb8bb9d2d8d0f931bbd15444c992a01fef03cfecf23492ccba.jpeg",
    title: "The brand tag",
    why: "Font weight, letter spacing and weave are the first things a faker gets wrong.",
    shoot: "Fill the frame with the label. Straight on, no angle, no shadow across the letters.",
  },
  {
    key: "care",
    img: IMG + "7208c52b3dfb69173fb4101c3610d10d4527a8b46fae3bdafdd211c041e06638.jpeg",
    title: "The inner care label",
    why: "Composition, country of origin and RN / factory codes date the piece and must match the era you claim.",
    shoot: "Flatten the label with your thumb so every line of small print is readable.",
  },
  {
    key: "stitching",
    img: IMG + "7ea6639399cd10bfaaad395eeafd8d88cd793478754d0c0d3ff77ab6896f8c86.jpeg",
    title: "A stitching macro",
    why: "Stitch density, thread colour and single-needle runs are almost impossible to copy cheaply.",
    shoot: "Get close on a seam or logo embroidery with light raking across it to show texture.",
  },
  {
    key: "serial",
    img: IMG + "45a4b034fd8c08b69e716cb2c42b54f17d033bd57f887417777e6848ab6cefb9.jpeg",
    title: "Hardware, serial or date code",
    why: "Engraved backs of buttons, zip pulls and date stamps are the strongest single piece of proof.",
    shoot: "Turn the button or zip over. Steady hands, hard light, one code per photo.",
  },
];

export const PROOF_RULES = {
  do: [
    "Three different close-ups of the same item — duplicates never verify",
    "Daylight or a single hard lamp, no filters, no colour boost",
    "Shoot the actual item you are shipping, not a catalogue photo",
    "Keep the piece flat and the camera parallel to the label",
  ],
  dont: [
    "Re-using your main product photo as the proof shot",
    "Mixing photos of two different garments in one listing",
    "Blurry, dim or heavily cropped label shots",
    "Claiming a brand you cannot photograph proof for — list it as Unbranded instead",
  ],
};

export const CARRIERS = ["USPS", "UPS", "FedEx", "DHL", "Royal Mail", "Canada Post", "Australia Post", "Other / local"];

export function trackingUrl(carrier = "", num = "") {
  const n = encodeURIComponent(num.trim());
  if (!n) return null;
  const c = carrier.toLowerCase();
  if (c.includes("usps")) return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${n}`;
  if (c.includes("ups")) return `https://www.ups.com/track?tracknum=${n}`;
  if (c.includes("fedex")) return `https://www.fedex.com/fedextrack/?trknbr=${n}`;
  if (c.includes("dhl")) return `https://www.dhl.com/en/express/tracking.html?AWB=${n}`;
  if (c.includes("royal")) return `https://www.royalmail.com/track-your-item#/tracking-results/${n}`;
  if (c.includes("canada")) return `https://www.canadapost-postescanada.ca/track-reperage/en#/resultList?searchFor=${n}`;
  if (c.includes("australia")) return `https://auspost.com.au/mypost/track/#/details/${n}`;
  return null;
}
